import {
  Activity,
  ArrowDown,
  ArrowUp,
  Banknote,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  ClipboardList,
  Download,
  Edit3,
  Eye,
  FileText,
  Filter,
  GripVertical,
  ImageIcon,
  MessageSquareText,
  PackageCheck,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  UserRoundPlus,
  UsersRound,
  BookOpenText
} from "lucide-react";
import { useMemo, useState } from "react";
import { EventItem } from "../components/records";
import { CustomerModal, OrderModal, customerStatusOptions, orderStatusOptions } from "../components/work/WorkModals";
import { ClientTime } from "../components/ClientTime";
import { QuoteModal } from "../components/work/QuoteModal";
import { CustomerDetailModal } from "../components/work/CustomerDetailModal";
import { Button, Card, EmptyState, PageHeader, SectionHeading } from "../components/ui";
import { useApp } from "../context/AppContext";
import { api } from "../lib/api";
import { downloadCustomerExport } from "../lib/customerExport";
import { formatDate, relativeDue } from "../lib/format";
import type { Contact, CustomerOrder, CustomerOrderStatus } from "../types";

type WorkTab = "customers" | "orders" | "activity";

const orderSequence: CustomerOrderStatus[] = [
  "INQUIRY", "REQUIREMENT_CONFIRMED", "QUOTING", "SAMPLING",
  "SAMPLE_CONFIRMATION", "DEPOSIT_PENDING", "PRODUCTION", "QUALITY_CHECK",
  "BALANCE_PENDING", "SHIPPING", "COMPLETED"
];

function customerStatusLabel(status: Contact["customer_status"]) {
  return customerStatusOptions.find(([value]) => value === status)?.[1] ?? status;
}

function parseImages(order: CustomerOrder) {
  try {
    const parsed = JSON.parse(order.images_json) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === "string") : [];
  } catch {
    return [];
  }
}

function parseFiles(order: CustomerOrder) {
  try {
    const parsed = JSON.parse(order.files_json ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is { url: string; name: string } =>
        typeof item === "object" && item !== null && typeof (item as { url: string }).url === "string"
    );
  } catch {
    return [];
  }
}

function formatOrderMoney(order: CustomerOrder) {
  if (!order.amount_minor) return "金额待确认";
  return new Intl.NumberFormat("zh-CN", {
    style: "currency",
    currency: order.currency,
    maximumFractionDigits: 2
  }).format(order.amount_minor / 100);
}

function orderProgress(status: CustomerOrderStatus) {
  const index = orderSequence.indexOf(status);
  return index < 0 ? 0 : Math.round(((index + 1) / orderSequence.length) * 100);
}

export function WorkPage() {
  const { data, openModal, mutate } = useApp();
  const [tab, setTab] = useState<WorkTab>("customers");
  const [query, setQuery] = useState("");
  const [importance, setImportance] = useState("ALL");
  const [orderStatus, setOrderStatus] = useState("ALL");
  const [customerEditor, setCustomerEditor] = useState<{ customer?: Contact } | null>(null);
  const [orderEditor, setOrderEditor] = useState<{ order?: CustomerOrder; contactId?: string } | null>(null);
  const [quoteTarget, setQuoteTarget] = useState<Contact | null>(null);
  const [detailTarget, setDetailTarget] = useState<Contact | null>(null);
  const [draggedCustomerId, setDraggedCustomerId] = useState<string | null>(null);
  const [exportedCount, setExportedCount] = useState<number | null>(null);
  const reportToFeishu = async (order: CustomerOrder) => {
    const statusLabel = orderStatusOptions.find(([value]) => value === order.status)?.[1] ?? order.status;
    const text = `订单汇报\n客户：${order.contact_name}\n款式：${order.title}\n状态：${statusLabel}\n${order.amount_minor ? `金额：${order.currency} ${(order.amount_minor / 100).toFixed(2)}\n` : ""}下一步：${order.next_action || "待定"}`;
    await mutate(() => api.feishuReport(text), "已发送到飞书群");
  };

  const syncOrderToFeishu = async (order: CustomerOrder) => {
    const result = await api.syncOrderToFeishu(order.id);
    if (result.url) window.open(result.url, "_blank");
  };

  if (!data) return null;

  const workEvents = useMemo(
    () => data.events.filter((event) => {
      try {
        const links = JSON.parse(event.links_json) as Array<{ entityType: string }>;
        return links.some((link) => link.entityType === "contact");
      } catch {
        return false;
      }
    }),
    [data.events]
  );

  const dueItems = [
    ...data.contacts.map((contact) => contact.next_followup),
    ...data.customerOrders.map((order) => order.next_followup)
  ].filter((date) => {
    const due = relativeDue(date);
    return due?.includes("逾期") || due?.includes("今天");
  });
  const activeOrders = data.customerOrders.filter((order) => !["COMPLETED", "CANCELLED"].includes(order.status));
  const priorityCustomers = data.contacts.filter((contact) => ["S", "A"].includes(contact.importance));
  const normalizedQuery = query.trim().toLowerCase();

  const filteredCustomers = data.contacts.filter((contact) => {
    const matchesImportance = importance === "ALL" || contact.importance === importance;
    const orders = data.customerOrders.filter((order) => order.contact_id === contact.id);
    const searchable = [
      contact.name, contact.organization, contact.country, contact.context, contact.next_action,
      ...orders.flatMap((order) => [order.title, order.order_code, order.requirements])
    ].join(" ").toLowerCase();
    return matchesImportance && (!normalizedQuery || searchable.includes(normalizedQuery));
  });

  const filteredOrders = data.customerOrders.filter((order) => {
    const matchesStatus = orderStatus === "ALL" || order.status === orderStatus;
    const searchable = [
      order.title, order.order_code, order.contact_name, order.contact_organization,
      order.requirements, order.next_action, order.note
    ].join(" ").toLowerCase();
    return matchesStatus && (!normalizedQuery || searchable.includes(normalizedQuery));
  });

  const reorder = async (orderedIds: string[]) => {
    await mutate(() => api.reorderContacts(orderedIds), "客户关注顺序已更新");
  };

  const moveCustomer = (contactId: string, direction: -1 | 1) => {
    const ids = data.contacts.map((contact) => contact.id);
    const index = ids.indexOf(contactId);
    const nextIndex = index + direction;
    if (index < 0 || nextIndex < 0 || nextIndex >= ids.length) return;
    [ids[index], ids[nextIndex]] = [ids[nextIndex]!, ids[index]!];
    void reorder(ids);
  };

  const dropCustomer = (targetId: string) => {
    if (!draggedCustomerId || draggedCustomerId === targetId) return;
    const ids = data.contacts.map((contact) => contact.id);
    const from = ids.indexOf(draggedCustomerId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    const [moved] = ids.splice(from, 1);
    if (!moved) return;
    ids.splice(to, 0, moved);
    setDraggedCustomerId(null);
    void reorder(ids);
  };

  const exportCustomers = () => {
    if (!data.contacts.length) return;
    downloadCustomerExport(data.contacts, data.customerOrders, data.profile?.timezone);
    setExportedCount(data.contacts.length);
    window.setTimeout(() => setExportedCount(null), 2400);
  };

  return (
    <div className="page page--work">
      <PageHeader
        eyebrow="WORK · 服装外贸跟单"
        title="客户与订单工作台"
        description="生意始于信任，增长来自每一次有结果的推进。"
        actions={<div className="button-group"><Button variant="secondary" onClick={exportCustomers} disabled={!data.contacts.length} title={data.contacts.length ? "导出全部客户与订单信息" : "登记客户后即可导出"} aria-label="导出客户信息" icon={exportedCount === null ? <Download size={16} /> : <CheckCircle2 size={16} />}>{exportedCount === null ? "导出客户" : `已导出 ${exportedCount} 位`}</Button><Button variant="secondary" onClick={() => setCustomerEditor({})} icon={<UserRoundPlus size={16} />}>登记客户</Button><Button onClick={() => setOrderEditor({})} icon={<Plus size={16} />}>登记订单</Button></div>}
      />

      <Card className="work-hero work-hero--trade">
        <div className="work-hero__icon"><BriefcaseBusiness size={24} /></div>
        <div><span className="eyebrow">外贸业务驾驶舱</span><h2>今天该推进谁，一眼就能看清</h2><p>客户重要性决定关注层级，手动排序决定真实处理顺序；每张订单单独记录要求与进度。</p></div>
        <div className="trade-flow">{["询盘", "报价", "打样", "生产", "发货"].map((label, index) => <span key={label}><i>{index + 1}</i>{label}{index < 4 && <ChevronRight size={13} />}</span>)}</div>
      </Card>

      <div className="work-metrics">
        <Card><span><UsersRound size={18} /></span><div><strong>{data.contacts.length}</strong><small>已登记客户</small></div></Card>
        <Card><span><CalendarClock size={18} /></span><div><strong>{dueItems.length}</strong><small>今天/逾期跟进</small></div></Card>
        <Card><span><ClipboardList size={18} /></span><div><strong>{activeOrders.length}</strong><small>进行中订单</small></div></Card>
        <Card><span><ShieldCheck size={18} /></span><div><strong>{priorityCustomers.length}</strong><small>S/A重要客户</small></div></Card>
      </div>

      <Card className="workbench-card">
        <div className="tabs tabs--embedded work-tabs">
          <button className={tab === "customers" ? "active" : ""} onClick={() => setTab("customers")}><UsersRound size={16} /> 客户跟进 <span>{data.contacts.length}</span></button>
          <button className={tab === "orders" ? "active" : ""} onClick={() => setTab("orders")}><PackageCheck size={16} /> 订单进度 <span>{data.customerOrders.length}</span></button>
          <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}><Activity size={16} /> 推进记录 <span>{workEvents.length}</span></button>
        </div>

        {tab !== "activity" && <div className="work-toolbar"><label><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索客户、订单、要求或编号" /></label><div><Filter size={15} />{tab === "customers" ? <select value={importance} onChange={(event) => setImportance(event.target.value)}><option value="ALL">全部重要性</option><option value="S">S · 核心</option><option value="A">A · 重要</option><option value="B">B · 常规</option><option value="C">C · 观察</option></select> : <select value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)}><option value="ALL">全部进度</option>{orderStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>}</div></div>}

        {tab === "customers" && <section className="work-section">
          <SectionHeading title="客户关注顺序" description="拖动卡片调整；手机上使用上下箭头。重要性与顺序可以随合作判断随时改变。" action={<Button size="sm" onClick={() => setCustomerEditor({})} icon={<Plus size={14} />}>登记客户</Button>} />
          {filteredCustomers.length ? <div className="customer-work-list">{filteredCustomers.map((contact) => {
            const orders = data.customerOrders.filter((order) => order.contact_id === contact.id);
            const active = orders.filter((order) => !["COMPLETED", "CANCELLED"].includes(order.status));
            const due = relativeDue(contact.next_followup);
            const thumbnails = orders.flatMap(parseImages).slice(0, 3);
            const index = data.contacts.findIndex((item) => item.id === contact.id);
            return <article className={`customer-work-card ${draggedCustomerId === contact.id ? "is-dragging" : ""}`} key={contact.id} onDragOver={(event) => event.preventDefault()} onDrop={() => dropCustomer(contact.id)}>
              <button className="customer-drag" draggable onDragStart={() => setDraggedCustomerId(contact.id)} onDragEnd={() => setDraggedCustomerId(null)} aria-label="拖动排序"><GripVertical size={18} /></button>
              <div className="customer-rank"><span>#{index + 1}</span><b className={`importance importance--${contact.importance.toLowerCase()}`}>{contact.importance}</b></div>
              <div className="customer-main">
                <div className="customer-title"><div><h3>{contact.name}</h3><p>{[contact.organization, contact.country].filter(Boolean).join(" · ") || "未填写公司与市场"}</p></div><span className={`customer-state customer-state--${contact.customer_status.toLowerCase()}`}>{customerStatusLabel(contact.customer_status)}</span></div>
                <div className="customer-time-line"><ClientTime country={contact.country} /></div>
                <dl className="customer-business-facts">
                  <div><dt>主营</dt><dd>{contact.main_products || "待登记"}</dd></div>
                  <div><dt>采购季节</dt><dd>{contact.purchase_season || "待登记"}</dd></div>
                  <div><dt>采购预算</dt><dd>{contact.annual_purchase || "待登记"}</dd></div>
                </dl>
                <div className="customer-context">{contact.context || "还没有填写客户背景与合作判断。"}</div>
                <div className="customer-next"><MessageSquareText size={15} /><div><small>下一步</small><strong>{contact.next_action || "尚未安排明确动作"}</strong></div></div>
                <div className="customer-tags"><span className={due?.includes("逾期") ? "overdue" : ""}><CalendarClock size={13} />{contact.next_followup ? `${formatDate(contact.next_followup)} · ${due ?? "待跟进"}` : "未设置跟进日期"}</span>{contact.contact_channel && <span><MessageSquareText size={13} />{contact.contact_channel}{contact.channel_account ? ` · ${contact.channel_account}` : ""}</span>}<span><PackageCheck size={13} />{orders.length} 单 · {active.length} 推进中</span></div>
              </div>
              <div className="customer-orders-glance"><div><strong>{orders.length}</strong><span>全部订单</span></div><div><strong>{active.length}</strong><span>正在推进</span></div>{thumbnails.length ? <div className="mini-order-images">{thumbnails.map((src, imageIndex) => <img key={`${src}-${imageIndex}`} src={src} alt="订单款式" />)}</div> : <div className="mini-order-empty"><ImageIcon size={17} />暂无款式图</div>}</div>
              <div className="customer-actions"><Button size="sm" onClick={() => setOrderEditor({ contactId: contact.id })} icon={<Plus size={14} />}>加订单</Button><Button size="sm" variant="secondary" onClick={() => setQuoteTarget(contact)} icon={<FileText size={14} />}>报价</Button><button onClick={() => setDetailTarget(contact)} title="查看客户画像"><Eye size={16} /></button><button onClick={() => setCustomerEditor({ customer: contact })} title="编辑客户"><Edit3 size={16} /></button><button onClick={() => moveCustomer(contact.id, -1)} disabled={index === 0} title="上移"><ArrowUp size={16} /></button><button onClick={() => moveCustomer(contact.id, 1)} disabled={index === data.contacts.length - 1} title="下移"><ArrowDown size={16} /></button><button className="danger" onClick={() => void mutate(() => api.remove("contacts", contact.id), "客户已移入回收站")} title="删除客户"><Trash2 size={16} /></button></div>
            </article>;
          })}</div> : <EmptyState icon={<UsersRound size={22} />} title={data.contacts.length ? "没有符合筛选的客户" : "还没有登记客户"} description="先登记真实客户，再把对方的款式需求和订单挂在客户名下。" actionLabel="登记第一个客户" onAction={() => setCustomerEditor({})} />}
        </section>}

        {tab === "orders" && <section className="work-section">
          <SectionHeading title="订单进度看板" description="进度可以直接选择，图片、要求、金额和下一步都保留在订单卡中。" action={<Button size="sm" onClick={() => setOrderEditor({})} icon={<Plus size={14} />}>登记订单</Button>} />
          {filteredOrders.length ? <div className="trade-order-grid">{filteredOrders.map((order) => {
            const images = parseImages(order);
            const due = relativeDue(order.next_followup);
            const progress = orderProgress(order.status);
            return <article className={`trade-order-card trade-order-card--${order.status.toLowerCase()}`} key={order.id}>
              <div className="order-cover">{images[0] ? <img src={images[0]} alt={order.title} /> : <div><ImageIcon size={25} /><span>暂无款式图</span></div>}{images.length > 1 && <b>+{images.length - 1}</b>}</div>
              <div className="order-card-body"><div className="order-card-top"><div><span>{order.contact_name}{order.contact_organization ? ` · ${order.contact_organization}` : ""}</span><h3>{order.title}</h3><small>{order.order_code || "未填写订单编号"}</small></div><button onClick={() => setOrderEditor({ order })}><Edit3 size={16} /></button></div>
                <div className="order-status-control"><select value={order.status} onChange={(event) => void mutate(() => api.updateCustomerOrderStatus(order.id, event.target.value), "订单进度已更新")}>{orderStatusOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><span>{progress}%</span></div>
                <div className="order-progress"><i style={{ width: `${progress}%` }} /></div>
                <div className="order-facts"><span><CircleDollarSign size={15} />{formatOrderMoney(order)}</span><span><PackageCheck size={15} />{order.quantity ? `${order.quantity.toLocaleString()} 件` : "数量待确认"}</span></div>
                <div className="order-requirements"><small>客户要求</small><p>{order.requirements || "还没有记录面料、工艺、包装或交期要求。"}</p></div>
                <div className="order-next"><CheckCircle2 size={15} /><div><small>下一步</small><strong>{order.next_action || "尚未安排明确动作"}</strong></div></div>
                {parseFiles(order).length > 0 && <div className="order-files">{parseFiles(order).map((item) => <a key={item.url} href={item.url} target="_blank" rel="noreferrer" title={item.name || "附件"}><FileText size={13} />{item.name || "附件"}</a>)}</div>}
                <div className="order-card-footer"><span className={due?.includes("逾期") ? "overdue" : ""}><CalendarClock size={14} />{order.next_followup ? `${formatDate(order.next_followup)} · ${due ?? "待跟进"}` : "未设置跟进日期"}</span><span className="order-card-actions"><button title="汇报到飞书群" onClick={() => void reportToFeishu(order)}><Send size={14} /></button><button title="存为飞书文档" onClick={() => void syncOrderToFeishu(order)}><BookOpenText size={14} /></button><button onClick={() => void mutate(() => api.remove("customer_orders", order.id), "订单已移入回收站")}><Trash2 size={15} /></button></span></div>
              </div>
            </article>;
          })}</div> : <EmptyState icon={<ClipboardList size={22} />} title={data.customerOrders.length ? "没有符合筛选的订单" : "还没有登记订单"} description="每个款式需求单独建一张订单卡，后续进度和要求就不会串单。" actionLabel="登记第一张订单" onAction={() => setOrderEditor({})} />}
        </section>}

        {tab === "activity" && <section className="work-section"><SectionHeading title="客户推进记录" description="只显示明确关联了客户的沟通结论与完成事实。" action={<Button size="sm" onClick={() => openModal("quick")} icon={<Plus size={14} />}>记录推进</Button>} />{workEvents.length ? <div className="embedded-timeline">{workEvents.map((event, index) => <EventItem event={event} last={index === workEvents.length - 1} key={event.id} />)}</div> : <EmptyState icon={<Activity size={22} />} title="尚无客户推进记录" description="完成沟通后记录结论、承诺与下一步，并关联对应客户。" actionLabel="写一条推进" onAction={() => openModal("quick")} />}</section>}
      </Card>

      <div className="privacy-note"><Banknote size={16} />订单金额只是业务记录，不会自动计入财务流水；实际收款时再在财务系统登记。</div>
      {customerEditor && <CustomerModal customer={customerEditor.customer} onClose={() => setCustomerEditor(null)} />}
      {orderEditor && <OrderModal contacts={data.contacts} order={orderEditor.order} presetContactId={orderEditor.contactId} onClose={() => setOrderEditor(null)} />}
      {quoteTarget && <QuoteModal contact={quoteTarget} onClose={() => setQuoteTarget(null)} />}
      {detailTarget && (
        <CustomerDetailModal
          contact={detailTarget}
          orders={data.customerOrders.filter((order) => order.contact_id === detailTarget.id)}
          events={data.events
            .filter((event) => {
              try {
                const links = JSON.parse(event.links_json) as Array<{ entityType: string; entityId: string }>;
                return links.some((link) => link.entityType === "contact" && link.entityId === detailTarget.id);
              } catch {
                return false;
              }
            })
            .map((event) => ({ id: event.id, title: event.title, occurred_at: event.occurred_at, content: event.factual_content }))}
          onClose={() => setDetailTarget(null)}
        />
      )}
    </div>
  );
}
